import React, { useState, useEffect } from 'react';
import SettingsService from '../../../services/SettingsService';
import { PageHeader, Card, Button, Toggle, Badge } from '../../ui';

const VerificationSettings = () => {
  const [form, setForm] = useState({ verify_sms_enabled: true, verify_email_enabled: true });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await SettingsService.getVerificationSettings();
        if (res.success) setForm((f) => ({ ...f, ...res.settings }));
      } catch (_) { /* ignore */ } finally { setLoading(false); }
    })();
  }, []);

  const save = async () => {
    setSaving(true);
    setMsg(null);
    try {
      const res = await SettingsService.updateVerificationSettings(form);
      setMsg({ ok: !!res.success, text: res.success ? 'Saved. Applies to the next sign-up.' : (res.error || 'Failed to save') });
    } catch (e) {
      setMsg({ ok: false, text: e.error || 'Failed to save' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="tw-py-16 tw-text-center"><i className="fa-solid fa-spinner fa-spin tw-text-2xl tw-text-brand" /></div>;
  }

  const { verify_sms_enabled: sms, verify_email_enabled: email } = form;
  const summary = sms && email
    ? 'New users must enter a code from both SMS and email.'
    : sms && !email
      ? 'New users must enter a code sent by SMS.'
      : !sms && email
        ? 'New users must enter a code sent by email.'
        : 'New users are registered immediately with no verification step.';

  return (
    <div className="tw-mx-auto tw-max-w-3xl">
      <PageHeader
        title="Account Verification"
        subtitle="Which channels a new sign-up must verify before the account is created"
        actions={<Button icon="fa-floppy-disk" onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save'}</Button>}
      />

      {msg && (
        <div className={`tw-mb-5 tw-rounded-xl tw-border tw-px-4 tw-py-3 tw-text-sm ${msg.ok ? 'tw-border-good/30 tw-bg-good/10 tw-text-good' : 'tw-border-live/30 tw-bg-live/10 tw-text-live'}`}>
          {msg.text}
        </div>
      )}

      <Card className="tw-mb-6">
        <div className="tw-flex tw-items-center tw-justify-between">
          <div>
            <div className="tw-font-semibold tw-text-heading"><i className="fa-solid fa-comment-sms tw-mr-2 tw-text-brand" />SMS verification</div>
            <div className="tw-text-sm tw-text-content-muted">Send a one-time code to the phone number and require it at sign-up. Needs Twilio configured.</div>
          </div>
          <Toggle checked={sms} onChange={(v) => setForm((f) => ({ ...f, verify_sms_enabled: v }))} />
        </div>
      </Card>

      <Card className="tw-mb-6">
        <div className="tw-flex tw-items-center tw-justify-between">
          <div>
            <div className="tw-font-semibold tw-text-heading"><i className="fa-solid fa-envelope tw-mr-2 tw-text-brand" />Email verification</div>
            <div className="tw-text-sm tw-text-content-muted">Send a one-time code to the email address and require it at sign-up. Needs SMTP configured.</div>
          </div>
          <Toggle checked={email} onChange={(v) => setForm((f) => ({ ...f, verify_email_enabled: v }))} />
        </div>
      </Card>

      <Card title="Result">
        <div className="tw-flex tw-items-center tw-gap-2 tw-text-sm">
          <Badge tone={sms ? 'good' : 'neutral'}>SMS {sms ? 'on' : 'off'}</Badge>
          <Badge tone={email ? 'good' : 'neutral'}>Email {email ? 'on' : 'off'}</Badge>
        </div>
        <p className="tw-mt-3 tw-text-sm tw-text-content-muted">{summary}</p>
        <p className="tw-mt-2 tw-text-xs tw-text-content-faint">
          When SMS is off the phone field is hidden on the sign-up form. Existing accounts are only asked to verify a
          channel that is currently required. Social (Google / Apple) sign-ins skip verification.
        </p>
      </Card>
    </div>
  );
};

export default VerificationSettings;
